/** Live-looking desk panels for the marketing page: real UI, no image payload, crisp on mobile. */
const Frame = ({ label, children }) => (
  <div className="rounded-sm border border-zinc-800 bg-zinc-950 overflow-hidden shadow-[0_24px_70px_-40px_rgba(16,185,129,0.45)]">
    <div className="flex items-center gap-1.5 px-3 py-2 border-b border-zinc-900 bg-black/70">
      <span className="h-2 w-2 rounded-full bg-zinc-700" />
      <span className="h-2 w-2 rounded-full bg-zinc-800" />
      <span className="h-2 w-2 rounded-full bg-zinc-800" />
      <span className="ml-2 font-mono text-[10px] text-zinc-600 truncate">{label}</span>
    </div>
    <div className="p-3 sm:p-4 overflow-x-auto">{children}</div>
  </div>
);

const Cell = ({ k, v, sub, tone = "text-zinc-100" }) => (
  <div className="border border-zinc-900 bg-black/40 rounded-sm p-2.5 sm:p-3">
    <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-600 font-semibold">{k}</div>
    <div className={`font-mono text-sm sm:text-lg mt-1 ${tone}`}>{v}</div>
    {sub && <div className="font-mono text-[9px] text-zinc-600 mt-0.5">{sub}</div>}
  </div>
);

const th = "text-left px-2 py-1.5 text-[9px] uppercase tracking-[0.1em] text-zinc-600 font-semibold";
const td = "px-2 py-2 font-mono text-[11px] text-zinc-300 border-t border-zinc-900 whitespace-nowrap";
const Tag = ({ children, tone }) => (
  <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-mono border ${tone}`}>{children}</span>
);
const brand = "border-emerald-800 bg-emerald-500/10 text-emerald-400";

export const DeskMock = () => (
  <Frame label="strictrr.com/trade">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
      <Cell k="Trades today" v="3 / 5" sub="Target $500" />
      <Cell k="Realised P&L" v="+$182.40" sub="Floating +$44.10" tone="text-emerald-400" />
      <Cell k="Loss limit left" v="$117.60" sub="of $300 · trailing" tone="text-amber-400" />
      <Cell k="Open / max" v="1 / 3" sub="Window 09:15–23:30" />
    </div>
    <table className="w-full mt-3 min-w-[430px]">
      <thead>
        <tr>
          {["Symbol", "Source", "Side", "Size", "Entry", "SL", "TP1", "P&L"].map((h) => (
            <th key={h} className={th}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className={td}>XAUUSD</td>
          <td className={td}><Tag tone={brand}>STRICT RR</Tag></td>
          <td className={td}><Tag tone="border-emerald-800 text-emerald-400">BUY</Tag></td>
          <td className={td}>0.24</td>
          <td className={td}>4638.85</td>
          <td className={td}>4638.85 <span className="text-emerald-500">BE</span></td>
          <td className={td}>4647.44 <Tag tone="border-emerald-800 text-emerald-500">HIT</Tag></td>
          <td className={`${td} text-emerald-400`}>+$44.10</td>
        </tr>
      </tbody>
    </table>
    <p className="mt-2 font-mono text-[10px] text-zinc-600">
      <span className="text-emerald-500">●</span> live 1s · TP1 booked 50% · stop moved to entry
    </p>
  </Frame>
);

export const BlockedMock = () => (
  <Frame label="risk engine · order rejected">
    <div className="border border-amber-800 bg-amber-500/10 px-3 py-2.5 rounded-sm">
      <p className="font-mono text-[11px] text-amber-300">
        Daily loss limit reached ($300.00) — the desk is closed for today.
      </p>
    </div>
    <div className="mt-3 space-y-1.5 font-mono text-[11px]">
      {[
        ["Risk per trade", "$100 fixed", "enforced"],
        ["Max trades / day", "5", "3 used"],
        ["Daily profit target", "$500", "armed"],
        ["Trading window", "09:15 – 23:30 IST", "open"],
        ["Kill switch", "off", "admin only"],
      ].map(([k, v, s]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="text-zinc-500 flex-1">{k}</span>
          <span className="text-zinc-200">{v}</span>
          <span className="text-emerald-600 text-[9px] uppercase tracking-[0.1em] w-20 text-right">
            {s}
          </span>
        </div>
      ))}
    </div>
  </Frame>
);

export const HistoryMock = () => (
  <Frame label="strictrr.com/history">
    <table className="w-full min-w-[460px]">
      <thead>
        <tr>{["Closed (IST)", "Symbol", "Side", "Exit", "Reason", "Source", "P&L"].map((h) => (
          <th key={h} className={th}>{h}</th>
        ))}</tr>
      </thead>
      <tbody>
        {[
          ["20:59", "XAUUSD", "BUY", "4642.30", "TP2", "+$96.00"],
          ["19:05", "XAUUSD", "SELL", "4618.50", "TP1", "+$50.00"],
          ["17:57", "US30", "SELL", "4635.03", "BREAK-EVEN", "+$0.00"],
          ["16:22", "XAUUSD", "BUY", "4630.10", "STOP LOSS", "-$100.00"],
        ].map(([t, s, side, exit, reason, pnl]) => (
          <tr key={t}>
            <td className={td}>{t}</td>
            <td className={td}>{s}</td>
            <td className={td}>
              <Tag tone={side === "BUY" ? "border-emerald-800 text-emerald-400" : "border-red-900 text-red-400"}>
                {side}
              </Tag>
            </td>
            <td className={td}>{exit}</td>
            <td className={td}>{reason}</td>
            <td className={td}><Tag tone={brand}>STRICT RR</Tag></td>
            <td className={`${td} ${pnl.startsWith("-") ? "text-red-400" : "text-emerald-400"}`}>
              {pnl}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </Frame>
);

export const CalendarMock = () => (
  <Frame label="history · calendar P&L">
    <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] mb-2">
      <span className="text-zinc-500">Net <span className="text-emerald-400">+$1,284.60</span></span>
      <span className="text-zinc-500">Green days <span className="text-emerald-400">11</span></span>
      <span className="text-zinc-500">Red days <span className="text-red-400">4</span></span>
    </div>
    <div className="grid grid-cols-7 gap-1">
      {Array.from({ length: 28 }).map((_, i) => {
        const pnl = [0, 0, 148, -62, 96, 0, 0, 212, 0, -100, 74, 320, 0, 0, 58, 0, -48, 186, 92,
          0, 0, 240, 118, -74, 166, 8, 0, 0][i];
        const tone = pnl > 0 ? "border-emerald-900/70 bg-emerald-500/[0.07] text-emerald-400"
          : pnl < 0 ? "border-red-900/60 bg-red-500/[0.06] text-red-400"
          : "border-zinc-900 text-zinc-700";
        return (
          <div key={i} className={`border rounded-sm px-1 py-1.5 sm:py-2.5 ${tone}`}>
            <div className="font-mono text-[8px] text-zinc-600">{i + 1}</div>
            <div className="font-mono text-[9px] sm:text-[10px] mt-0.5">
              {pnl ? `${pnl > 0 ? "+" : ""}$${Math.abs(pnl)}` : "—"}
            </div>
          </div>
        );
      })}
    </div>
  </Frame>
);

export const AdminMock = () => (
  <Frame label="strictrr.com/admin">
    <table className="w-full min-w-[420px]">
      <thead>
        <tr>{["Trader", "Pod", "Trades", "Today", "Limit left", "State"].map((h) => (
          <th key={h} className={th}>{h}</th>
        ))}</tr>
      </thead>
      <tbody>
        {[
          ["arjun@…", "pod1 · 7701", "3 / 5", "+$182", "$117", "trading", "text-emerald-400"],
          ["neha@…", "pod1 · 7702", "5 / 5", "+$540", "target hit", "blocked", "text-amber-400"],
          ["vikram@…", "pod1 · 7703", "2 / 5", "-$300", "$0", "day closed", "text-red-400"],
          ["sara@…", "pod2 · 7701", "1 / 5", "+$64", "$236", "trading", "text-emerald-400"],
        ].map(([who, pod, trades, today, left, state, tone]) => (
          <tr key={who}>
            <td className={td}>{who}</td>
            <td className={`${td} text-zinc-500`}>{pod}</td>
            <td className={td}>{trades}</td>
            <td className={`${td} ${today.startsWith("-") ? "text-red-400" : "text-emerald-400"}`}>
              {today}
            </td>
            <td className={td}>{left}</td>
            <td className={`${td} ${tone}`}>{state}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="mt-2 font-mono text-[10px] text-zinc-600">
      Admin sets the rules · never places a trade
    </p>
  </Frame>
);
